import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useBranch } from '@/context/BranchContext';
import { useSalesSettings } from '@/hooks/useSalesSettings';
import { useInvoiceAdmin, type InvoiceSearchFilters } from '@/hooks/useInvoiceAdmin';
import { formatMoney, formatDateTime } from '@/lib/format';
import { invoiceStatusMeta, PAYMENT_LABELS } from '@/lib/types';
import InvoiceDetailModal from './components/InvoiceDetailModal';

const selectClass =
  'rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';

export default function InvoicesPage() {
  const { branches } = useBranch();
  const { settings } = useSalesSettings();
  const { results, searching, error, searched, search, clear } = useInvoiceAdmin();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [cashiers, setCashiers] = useState<{ id: string; name: string; role: string }[]>([]);

  const [filters, setFilters] = useState<InvoiceSearchFilters>({
    invoiceNumber: '',
    customerName: '',
    date: '',
    cashierId: null,
    branchId: null,
  });

  const currencySymbol = settings?.currency_symbol ?? 'RD$';

  useEffect(() => {
    let active = true;
    supabase
      .from('profiles')
      .select('id, first_name, last_name, username, email, role')
      .order('first_name')
      .then(({ data }) => {
        if (!active || !data) return;
        setCashiers(
          data.map((p: Record<string, unknown>) => ({
            id: String(p.id),
            name:
              [p.first_name, p.last_name].filter(Boolean).join(' ') ||
              (p.username as string) ||
              (p.email as string) ||
              'Usuario',
            role: ['super_admin', 'administrador'].includes(String(p.role))
              ? 'Administrador'
              : 'Cajero',
          }))
        );
      });
    return () => {
      active = false;
    };
  }, []);

  const patch = (partial: Partial<InvoiceSearchFilters>) =>
    setFilters((prev) => ({ ...prev, ...partial }));

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    search(filters);
  };

  const handleReset = () => {
    setFilters({ invoiceNumber: '', customerName: '', date: '', cashierId: null, branchId: null });
    clear();
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          Gestión de facturas
        </h1>
        <p className="mt-1 text-sm text-foreground-600">
          Busca facturas, realiza reembolsos totales o parciales y anula/elimina con registro de auditoría.
        </p>
      </div>

      {/* Filtros de búsqueda */}
      <form
        onSubmit={handleSearch}
        className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Número de factura</label>
            <input
              type="text"
              value={filters.invoiceNumber}
              onChange={(e) => patch({ invoiceNumber: e.target.value })}
              placeholder="Ej: FAC000125"
              className={`${selectClass} w-full`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Cliente</label>
            <input
              type="text"
              value={filters.customerName}
              onChange={(e) => patch({ customerName: e.target.value })}
              placeholder="Nombre del cliente"
              className={`${selectClass} w-full`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Fecha</label>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => patch({ date: e.target.value })}
              className={`${selectClass} w-full cursor-pointer`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Cajero</label>
            <select
              value={filters.cashierId ?? ''}
              onChange={(e) => patch({ cashierId: e.target.value || null })}
              className={`${selectClass} w-full cursor-pointer`}
            >
              <option value="">Todos los usuarios</option>
              {cashiers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.role}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Sucursal</label>
            <select
              value={filters.branchId ?? ''}
              onChange={(e) => patch({ branchId: e.target.value || null })}
              className={`${selectClass} w-full cursor-pointer`}
            >
              <option value="">Todas las sucursales</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={handleReset}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            Limpiar
          </button>
          <button
            type="submit"
            disabled={searching}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-search-line" /> {searching ? 'Buscando...' : 'Buscar factura'}
          </button>
        </div>
      </form>

      {error && (
        <div className="rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {/* Resultados */}
      {searching ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : searched && results.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 p-10 text-center">
          <i className="ri-file-search-line text-4xl text-foreground-400" />
          <p className="mt-3 text-sm text-foreground-600">No se encontraron facturas con esos criterios.</p>
        </div>
      ) : results.length > 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-foreground-950">Resultados</h2>
            <span className="text-xs text-foreground-500">{results.length} facturas</span>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="text-foreground-600 text-left border-b border-background-200">
                <tr>
                  <th className="py-2 font-medium">Factura</th>
                  <th className="py-2 font-medium">Fecha</th>
                  <th className="py-2 font-medium">Cliente</th>
                  <th className="py-2 font-medium">Cajero</th>
                  <th className="py-2 font-medium">Sucursal</th>
                  <th className="py-2 font-medium text-right">Total</th>
                  <th className="py-2 font-medium text-center">Estado</th>
                  <th className="py-2 font-medium text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {results.map((s) => {
                  const meta = invoiceStatusMeta(s.status);
                  return (
                    <tr key={s.id} className="hover:bg-background-100/60 transition">
                      <td className="py-2.5 font-medium text-foreground-950">{s.invoice_number}</td>
                      <td className="py-2.5 text-foreground-700">{formatDateTime(s.created_at)}</td>
                      <td className="py-2.5 text-foreground-700">{s.customer_name || 'Cliente general'}</td>
                      <td className="py-2.5 text-foreground-700">{s.cashier_name || '—'}</td>
                      <td className="py-2.5 text-foreground-700">{s.branch?.name || '—'}</td>
                      <td className="py-2.5 text-right font-medium text-foreground-950">
                        {formatMoney(Number(s.total), currencySymbol)}
                      </td>
                      <td className="py-2.5 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${meta.badge}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-2.5 text-right">
                        <button
                          onClick={() => setDetailId(s.id)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                        >
                          <i className="ri-eye-line" /> Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-background-300 p-10 text-center">
          <i className="ri-file-list-3-line text-4xl text-foreground-400" />
          <p className="mt-3 text-sm text-foreground-600">
            Usa los filtros para buscar una factura por número, cliente, fecha, cajero o sucursal.
          </p>
        </div>
      )}

      {detailId && (
        <InvoiceDetailModal
          saleId={detailId}
          currencySymbol={currencySymbol}
          onClose={() => setDetailId(null)}
          onDone={() => search(filters)}
        />
      )}
    </div>
  );
}